import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useHistory } from 'react-router-dom';
import API_BASE_URL from '../config';
import { 
    Search, Plus, MapPin, Tag, Phone, Mail, 
    Trash2, Edit3, ChevronRight, X, Star,
    Instagram, Facebook, LayoutGrid, Filter,
    ArrowLeft, Calendar, Save, Upload, Package
} from 'lucide-react';
import VendorAvailabilityManager from '../components/VendorAvailabilityManager';
import SearchableSelect from '../components/SearchableSelect';
import './SuperAdminMarketplace.css';

interface MarketVendor {
    _id: string;
    name: string;
    category: string;
    primaryContact: string;
    secondaryContact?: string;
    email?: string;
    city?: string;
    address?: string;
    description?: string;
    priceRange: string;
    rating: number;
    status: string;
    images?: string[];
    isVerified?: boolean;
    vendorType?: 'product' | 'service';
    instagramUrl?: string;
    facebookUrl?: string;
    packages?: Array<{
        name: string;
        price: number;
        priceUnit?: string;
        description?: string;
        features?: string[];
    }>;
}

const ICON_MAP: { [key: string]: any } = {
    'Search': Search, 'Plus': Plus, 'MapPin': MapPin, 'Tag': Tag, 'Phone': Phone, 
    'Mail': Mail, 'Trash2': Trash2, 'Edit3': Edit3, 'ChevronRight': ChevronRight, 
    'X': X, 'Star': Star, 'Instagram': Instagram, 'Facebook': Facebook, 
    'LayoutGrid': LayoutGrid, 'Filter': Filter, 'ArrowLeft': ArrowLeft, 
    'Calendar': Calendar, 'Save': Save, 'Upload': Upload
};

const SuperAdminMarketplace: React.FC = () => {
    const { accessToken, user, loading: authLoading } = useAuth();
    const history = useHistory();
    const [vendors, setVendors] = useState<MarketVendor[]>([]);
    const [packages, setPackages] = useState<Array<{ name: string; price: number; priceUnit?: string; description?: string; features?: string[] }>>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [selectedVendorForAvail, setSelectedVendorForAvail] = useState<{id: string, name: string} | null>(null);
    const [selectedVendorForPkgs, setSelectedVendorForPkgs] = useState<{id: string, name: string, packages: any[]} | null>(null);
    const [isCreating, setIsCreating] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCities, setSelectedCities] = useState<string[]>([]);
    const [selectedCategory, setSelectedCategory] = useState('All');
    const [activeTab, setActiveTab] = useState<'vendors' | 'cities' | 'categories'>('vendors');
    const [cities, setCities] = useState<{ _id: string, name: string, state?: string, isActive: boolean }[]>([]);
    const [categories, setCategories] = useState<{ _id: string, name: string, icon?: string }[]>([]);
    const [priceRanges, setPriceRanges] = useState<string[]>([]);
    
    const [cityFormData, setCityFormData] = useState({ name: '', state: '', isActive: true });
    const [isEditingCity, setIsEditingCity] = useState<string | null>(null);

    const [categoryFormData, setCategoryFormData] = useState({ name: '', icon: 'Tag' });
    const [isEditingCategory, setIsEditingCategory] = useState<string | null>(null);
    
    const [formData, setFormData] = useState({
        name: '',
        category: '',
        primaryContact: '',
        secondaryContact: '',
        email: '',
        city: [] as string[],
        address: '',
        description: '',
        instagramUrl: '',
        facebookUrl: '',
        rating: 4.5,
        isVerified: false,
        vendorType: 'service'
    });

    const [selectedPriceRanges, setSelectedPriceRanges] = useState<string[]>([]);
    const [imageFiles, setImageFiles] = useState<File[]>([]);
    const [imagePreviews, setImagePreviews] = useState<string[]>([]);
    const [existingImages, setExistingImages] = useState<string[]>([]);

    useEffect(() => {
        // Cleanup object URLs when component unmounts or previews change
        return () => {
            imagePreviews.forEach(url => URL.revokeObjectURL(url));
        };
    }, [imagePreviews]);

    useEffect(() => {
        if (!authLoading && (!user || user.role !== 'superadmin')) {
            history.push('/superadmin/login');
        }
    }, [user, authLoading, history]);

    const fetchVendors = async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE_URL}/api/market-vendors`, {
                headers: { 'Authorization': `Bearer ${accessToken}` }
            });
            const data = await res.json();
            if (res.ok) {
                setVendors(data.vendors);
            }
        } catch (err) {
            console.error(err);
            setError('Failed to fetch vendors');
        } finally {
            setLoading(false);
        }
    };

    const fetchCities = async () => {
        try {
            const res = await fetch(`${API_BASE_URL}/api/superadmin/cities`, {
                headers: { 'Authorization': `Bearer ${accessToken}` }
            });
            const data = await res.json();
            if (data.success) {
                setCities(data.cities);
            }
        } catch (err) {
            console.error('Failed to fetch cities:', err);
        }
    };

    const fetchCategories = async () => {
        try {
            const res = await fetch(`${API_BASE_URL}/api/superadmin/categories`, {
                headers: { 'Authorization': `Bearer ${accessToken}` }
            });
            const data = await res.json();
            if (data.success) {
                setCategories(data.categories);
            }
        } catch (err) {
            console.error('Failed to fetch categories:', err);
        }
    };

    const fetchPriceRanges = async () => {
        try {
            const res = await fetch(`${API_BASE_URL}/api/market-vendors/price-ranges`, {
                headers: { 'Authorization': `Bearer ${accessToken}` }
            });
            const data = await res.json();
            if (data.success) {
                setPriceRanges(data.priceRanges);
            }
        } catch (err) {
            console.error('Failed to fetch price ranges:', err);
        }
    };

    useEffect(() => {
        if (accessToken) {
            fetchVendors();
            fetchCities();
            fetchCategories();
            fetchPriceRanges();
        }
    }, [accessToken]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const target = e.target as HTMLInputElement;
        const value = target.type === 'checkbox' ? target.checked : target.value;
        setFormData({ ...formData, [target.name]: value });
    };

    const togglePriceRange = (range: string) => {
        setSelectedPriceRanges(prev => 
            prev.includes(range) 
                ? prev.filter(r => r !== range) 
                : [...prev, range]
        );
    };

    const handleEditCity = (city: any) => {
        setCityFormData({ name: city.name, state: city.state || '', isActive: city.isActive });
        setIsEditingCity(city._id);
    };

    const handleCitySubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            const url = isEditingCity 
                ? `${API_BASE_URL}/api/superadmin/cities/${isEditingCity}`
                : `${API_BASE_URL}/api/superadmin/cities`;
            const method = isEditingCity ? 'PUT' : 'POST';

            const res = await fetch(url, {
                method,
                headers: { 
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${accessToken}`
                },
                body: JSON.stringify(cityFormData)
            });

            if (res.ok) {
                setSuccess(isEditingCity ? 'City updated' : 'City added');
                setIsEditingCity(null);
                setCityFormData({ name: '', state: '', isActive: true });
                fetchCities();
                setTimeout(() => setSuccess(''), 3000);
            } else {
                const data = await res.json();
                setError(data.message || 'Operation failed');
            }
        } catch (err) {
            setError('Something went wrong');
        }
    };

    const handleDeleteCity = async (id: string) => {
        if (!window.confirm('Delete this city?')) return;
        try {
            const res = await fetch(`${API_BASE_URL}/api/superadmin/cities/${id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${accessToken}` }
            });
            if (res.ok) {
                fetchCities();
                setSuccess('City deleted');
                setTimeout(() => setSuccess(''), 3000);
            }
        } catch (err) {
            setError('Delete failed');
        }
    };

    const handleEditCategory = (category: any) => {
        setCategoryFormData({ name: category.name, icon: category.icon || 'Tag' });
        setIsEditingCategory(category._id);
    };

    const handleCategorySubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            const url = isEditingCategory 
                ? `${API_BASE_URL}/api/superadmin/categories/${isEditingCategory}`
                : `${API_BASE_URL}/api/superadmin/categories`;
            const method = isEditingCategory ? 'PUT' : 'POST';

            const res = await fetch(url, {
                method,
                headers: { 
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${accessToken}`
                },
                body: JSON.stringify(categoryFormData)
            });

            if (res.ok) {
                setSuccess(isEditingCategory ? 'Category updated' : 'Category added');
                setIsEditingCategory(null);
                setCategoryFormData({ name: '', icon: 'Tag' });
                fetchCategories();
                setTimeout(() => setSuccess(''), 3000);
            } else {
                const data = await res.json();
                setError(data.message || 'Operation failed');
            }
        } catch (err) {
            setError('Something went wrong');
        }
    };

    const handleDeleteCategory = async (id: string) => {
        if (!window.confirm('Delete this category? Vendors in this category will not be deleted but will have an unlinked category.')) return;
        try {
            const res = await fetch(`${API_BASE_URL}/api/superadmin/categories/${id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${accessToken}` }
            });
            if (res.ok) {
                fetchCategories();
                setSuccess('Category deleted');
                setTimeout(() => setSuccess(''), 3000);
            }
        } catch (err) {
            setError('Delete failed');
        }
    };

    const handleEdit = (vendor: MarketVendor) => {
        setFormData({
            name: vendor.name || '',
            category: vendor.category || '',
            primaryContact: vendor.primaryContact || '',
            secondaryContact: vendor.secondaryContact || '',
            email: vendor.email || '',
            city: vendor.city ? vendor.city.split(',').map(c => c.trim()).filter(Boolean) : [],
            address: vendor.address || '',
            description: vendor.description || '',
            instagramUrl: vendor.instagramUrl || '',
            facebookUrl: vendor.facebookUrl || '',
            rating: vendor.rating || 4.5,
            isVerified: vendor.isVerified || false,
            vendorType: (vendor.vendorType as 'product' | 'service') || 'service'
        });
        
        if (vendor.priceRange) {
            const ranges = vendor.priceRange.split(',').map(s => s.trim());
            setSelectedPriceRanges(ranges.filter(r => priceRanges.includes(r)));
        } else {
            setSelectedPriceRanges([]);
        }
        
        setPackages(vendor.packages || []);
        setExistingImages(vendor.images || []);
        setImageFiles([]);
        setImagePreviews([]);
        setEditingId(vendor._id);
        setIsCreating(true);
    };

    const removeExistingImage = (index: number) => {
        setExistingImages(prev => prev.filter((_, i) => i !== index));
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            const files = Array.from(e.target.files);
            
            if (files.length > 5) {
                setError('You can only upload a maximum of 5 images');
                e.target.value = ''; // Reset input
                return;
            }

            // Cleanup old previews
            imagePreviews.forEach(url => URL.revokeObjectURL(url));
            
            setImageFiles(files);
            
            // Create new previews
            const previews = files.map(file => URL.createObjectURL(file));
            setImagePreviews(previews);
            setError('');
        }
    };

    const removeImage = (index: number) => {
        const newFiles = [...imageFiles];
        const newPreviews = [...imagePreviews];
        
        // Revoke the URL being removed
        URL.revokeObjectURL(newPreviews[index]);
        
        newFiles.splice(index, 1);
        newPreviews.splice(index, 1);
        
        setImageFiles(newFiles);
        setImagePreviews(newPreviews);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        setSuccess('');
        try {
            const submitData = new FormData();
            Object.entries(formData).forEach(([key, value]) => {
                if (key === 'city' && Array.isArray(value)) {
                    submitData.append(key, value.join(', '));
                } else {
                    submitData.append(key, value.toString());
                }
            });
            submitData.append('priceRange', selectedPriceRanges.join(', '));
            if (editingId) {
                submitData.append('existingImages', JSON.stringify(existingImages));
            }
            imageFiles.forEach(file => {
                submitData.append('images', file);
            });

            const url = editingId 
                ? `${API_BASE_URL}/api/market-vendors/${editingId}`
                : `${API_BASE_URL}/api/market-vendors`;
            
            const method = editingId ? 'PUT' : 'POST';

            const res = await fetch(url, {
                method,
                headers: {
                    'Authorization': `Bearer ${accessToken}`
                },
                body: submitData
            });
            if (res.ok) {
                setSuccess(editingId ? 'Vendor updated successfully' : 'Vendor added successfully');
                
                // Clear form but keep drawer open for a moment to show success
                setTimeout(() => {
                    setIsCreating(false);
                    setEditingId(null);
                    setFormData({
                        name: '', category: '', primaryContact: '', secondaryContact: '', email: '', city: [], address: '', description: '', instagramUrl: '', facebookUrl: '', rating: 4.5, isVerified: false, vendorType: 'service'
                    });
                    setSelectedPriceRanges([]);
                    setPackages([]);
                    setImageFiles([]);
                    setImagePreviews([]);
                    setExistingImages([]);
                    setSuccess('');
                    fetchVendors();
                }, 2000);
            } else {
                const data = await res.json();
                setError(data.message || 'Failed to process vendor');
            }
        } catch (err) {
            setError('Error connecting to server');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id: string) => {
        if (!window.confirm('Delete this vendor from the Global Marketplace?')) return;
        try {
            const res = await fetch(`${API_BASE_URL}/api/market-vendors/${id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${accessToken}` }
            });
            if (res.ok) {
                alert('Vendor deleted successfully');
                fetchVendors();
            }
        } catch (err) {
            console.error(err);
        }
    };

    const filteredVendors = vendors.filter(v => {
        const vendorName = v.name || '';
        const vendorCity = v.city || '';
        const vendorCategory = (v.category || '').trim();
        const searchTermLower = searchTerm.toLowerCase();

        const matchesSearch = vendorName.toLowerCase().includes(searchTermLower) || 
                              vendorCity.toLowerCase().includes(searchTermLower) ||
                              vendorCategory.toLowerCase().includes(searchTermLower);

        const matchesCategory = selectedCategory === 'All' || 
                               vendorCategory.toLowerCase() === selectedCategory.toLowerCase();
                               
        return matchesSearch && matchesCategory && (selectedCities.length === 0 || selectedCities.some(city => vendorCity.toLowerCase().includes(city.toLowerCase())));
    });

    return (
        <div className="super-admin-marketplace">
            <header className="marketplace-header">
                <div className="header-left">
                    <h1>Marketplace Management</h1>
                    <p>Curate and manage global wedding service providers • <span className="highlight-text">{vendors.length} Total Vendors</span></p>
                </div>
                <div className="header-actions">
                    <button className="btn-luxury-outline" onClick={() => history.push('/superadmin/dashboard')} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <ArrowLeft size={18} /> Back
                    </button>
                    <button className="btn-luxury" onClick={() => { 
                        setEditingId(null); 
                        setIsCreating(true); 
                        setError(''); 
                        setSuccess(''); 
                        setFormData({
                            name: '', category: '', primaryContact: '', secondaryContact: '', email: '', city: [], address: '', description: '', instagramUrl: '', facebookUrl: '', rating: 4.5, isVerified: false, vendorType: 'service'
                        });
                        setSelectedPriceRanges([]);
                        setPackages([]);
                        setImageFiles([]);
                        setImagePreviews([]);
                        setExistingImages([]);
                    }} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Plus size={18} /> Add New Vendor
                    </button>
                </div>
            </header>

            <div className="marketplace-container">
                {/* Sidebar */}
                <aside className="category-sidebar">
                    <h3>Categories</h3>
                    <div className="category-list">
                        <div 
                            className={`category-item ${selectedCategory === 'All' ? 'active' : ''}`}
                            onClick={() => setSelectedCategory('All')}
                        >
                            <LayoutGrid size={16} />
                            <span>All</span>
                            <span style={{ marginLeft: 'auto', fontSize: '0.75rem', opacity: 0.6 }}>({vendors.length})</span>
                        </div>
                        {categories.map(cat => {
                            const Icon = ICON_MAP[cat.icon || 'Tag'] || Tag;
                            const count = vendors.filter(v => (v.category || '').toLowerCase().trim() === cat.name.toLowerCase().trim()).length;
                            return (
                                <div 
                                    key={cat._id} 
                                    className={`category-item ${selectedCategory === cat.name ? 'active' : ''}`}
                                    onClick={() => setSelectedCategory(cat.name)}
                                >
                                    <Icon size={16} />
                                    <span>{cat.name}</span>
                                    <span style={{ marginLeft: 'auto', fontSize: '0.75rem', opacity: 0.6 }}>({count})</span>
                                </div>
                            );
                        })}
                    </div>
                </aside>

                <div className="marketplace-main-content">
                    <div className="marketplace-tabs">
                    <button 
                        className={`market-tab-btn ${activeTab === 'vendors' ? 'active' : ''}`}
                        onClick={() => setActiveTab('vendors')}
                    >
                        Vendor Marketplace
                    </button>
                    <button 
                        className={`market-tab-btn ${activeTab === 'cities' ? 'active' : ''}`}
                        onClick={() => setActiveTab('cities')}
                    >
                        Manage Cities
                    </button>
                    <button 
                        className={`market-tab-btn ${activeTab === 'categories' ? 'active' : ''}`}
                        onClick={() => setActiveTab('categories')}
                    >
                        Manage Categories
                    </button>
                </div>
                {activeTab === 'vendors' ? (
                    <main className="marketplace-feed">
                        <div className="search-controls-premium">
                            <div className="search-bar-premium">
                                <Search size={20} className="text-muted" />
                                <input 
                                    type="text" 
                                    placeholder="Search by name or service..." 
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>
                            <div className="city-filter-premium">
                                <SearchableSelect
                                    isMulti={true}
                                    options={cities.map(c => ({ value: c.name, label: c.name }))}
                                    value={selectedCities}
                                    onChange={setSelectedCities}
                                    placeholder="Filter by Cities..."
                                />
                            </div>
                        </div>

                        <div className="vendor-grid">
                            {loading ? (
                                <p>Loading marketplace data...</p>
                            ) : filteredVendors.length === 0 ? (
                                <div className="empty-state">
                                    <Search size={48} />
                                    <p>No vendors found matching your criteria.</p>
                                </div>
                            ) : (
                                filteredVendors.map(vendor => (
                                    <div key={vendor._id} className="premium-vendor-card">
                                        <div className="vendor-image-wrapper">
                                            <img 
                                                src={vendor.images?.[0] || 'https://images.unsplash.com/photo-1519741497674-611481863552?q=80&w=2070&auto=format&fit=crop'} 
                                                alt={vendor.name} 
                                            />
                                        </div>
                                        <div className="vendor-content-premium">
                                            <div className="vendor-category-tag">{vendor.category}</div>
                                            <div className="vendor-title-row">
                                                <h3>{vendor.name}</h3>
                                                <div className="vendor-rating">
                                                    <Star size={12} fill="currentColor" />
                                                    {vendor.rating || 4.5}
                                                </div>
                                            </div>
                                            
                                            <div className="vendor-details-row">
                                                <span className="detail-pill">
                                                    <MapPin size={12} /> {vendor.city || 'National'}
                                                </span>
                                                <span className="detail-pill">
                                                    <Tag size={12} /> {vendor.priceRange || 'Contact for Price'}
                                                </span>
                                            </div>

                                            <p className="vendor-desc-short">
                                                {vendor.description || 'Premium wedding service provider offering exceptional quality and personalized experiences for your special day.'}
                                            </p>

                                            <div className="vendor-card-actions" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: '8px' }}>
                                                <button className="btn-premium-edit" onClick={() => handleEdit(vendor)} title="Edit Details" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '8px' }}>
                                                    <Edit3 size={16} />
                                                </button>
                                                <button 
                                                    className="btn-premium-edit" 
                                                    style={{ border: '1px solid #d4af37', background: 'transparent', color: '#d4af37', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '8px' }}
                                                    onClick={() => setSelectedVendorForPkgs({ id: vendor._id, name: vendor.name, packages: vendor.packages || [] })}
                                                    title="Manage Custom Packages & Plans"
                                                >
                                                    <Package size={16} />
                                                </button>
                                                <button 
                                                    className="btn-premium-edit" 
                                                    style={{ border: '1px solid #d4af37', background: 'transparent', color: '#d4af37', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '8px' }}
                                                    onClick={() => setSelectedVendorForAvail({ id: vendor._id, name: vendor.name })}
                                                    title="Manage Availability Slots"
                                                >
                                                    <Calendar size={16} />
                                                </button>
                                                <button className="btn-premium-delete" onClick={() => handleDelete(vendor._id)} title="Delete Vendor" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '8px' }}>
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </main>
                ) : activeTab === 'cities' ? (
                    <main className="city-management-section">
                        <div className="city-admin-header">
                            <h3>Master City Table</h3>
                            <p className="text-muted">Cities added here will be available for vendors and agency discovery.</p>
                        </div>

                        <div className="city-mgmt-layout">
                            <div className="city-form-card glass-card">
                                <h4>{isEditingCity ? 'Edit City' : 'Add New City'}</h4>
                                <form onSubmit={handleCitySubmit} className="city-admin-form">
                                    <div className="premium-form-group">
                                        <label>City Name</label>
                                        <input 
                                            className="premium-input"
                                            type="text"
                                            value={cityFormData.name}
                                            onChange={(e) => setCityFormData({ ...cityFormData, name: e.target.value })}
                                            required
                                            placeholder="e.g. Indore"
                                        />
                                    </div>
                                    <div className="premium-form-group">
                                        <label>State</label>
                                        <input 
                                            className="premium-input"
                                            type="text"
                                            value={cityFormData.state}
                                            onChange={(e) => setCityFormData({ ...cityFormData, state: e.target.value })}
                                            placeholder="e.g. Madhya Pradesh"
                                        />
                                    </div>
                                    <div className="city-form-actions">
                                        <button type="submit" className="btn-premium">
                                            {isEditingCity ? 'Update City' : 'Add City'}
                                        </button>
                                        {isEditingCity && (
                                            <button 
                                                type="button" 
                                                className="btn-cancel"
                                                onClick={() => {
                                                    setIsEditingCity(null);
                                                    setCityFormData({ name: '', state: '', isActive: true });
                                                }}
                                            >
                                                Cancel
                                            </button>
                                        )}
                                    </div>
                                </form>
                            </div>

                            <div className="city-list-card glass-card">
                                <table className="city-table">
                                    <thead>
                                        <tr>
                                            <th>City Name</th>
                                            <th>State</th>
                                            <th>Status</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {cities.map(city => (
                                            <tr key={city._id}>
                                                <td className="font-bold">{city.name}</td>
                                                <td>{city.state || '-'}</td>
                                                <td>
                                                    <span className={`status-pill ${city.isActive ? 'active' : 'inactive'}`}>
                                                        {city.isActive ? 'Active' : 'Inactive'}
                                                    </span>
                                                </td>
                                                <td className="city-actions">
                                                    <button onClick={() => handleEditCity(city)} className="btn-icon">
                                                        <Edit3 size={14} />
                                                    </button>
                                                    <button onClick={() => handleDeleteCity(city._id)} className="btn-icon delete">
                                                        <Trash2 size={14} />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </main>
                ) : (
                    <main className="category-management-section">
                        <div className="category-admin-header">
                            <h3>Marketplace Categories</h3>
                            <p className="text-muted">Manage global categories used to group vendors across the platform.</p>
                        </div>

                        <div className="category-mgmt-layout">
                            <div className="category-form-card glass-card">
                                <h4>{isEditingCategory ? 'Edit Category' : 'Add New Category'}</h4>
                                <form onSubmit={handleCategorySubmit} className="category-admin-form">
                                    <div className="premium-form-group">
                                        <label>Category Name</label>
                                        <input 
                                            className="premium-input"
                                            type="text"
                                            value={categoryFormData.name}
                                            onChange={(e) => setCategoryFormData({ ...categoryFormData, name: e.target.value })}
                                            required
                                            placeholder="e.g. Luxury Venues"
                                        />
                                    </div>
                                    <div className="premium-form-group">
                                        <label>Icon Identifier</label>
                                        <select 
                                            className="premium-input"
                                            value={categoryFormData.icon}
                                            onChange={(e) => setCategoryFormData({ ...categoryFormData, icon: e.target.value })}
                                        >
                                            {Object.keys(ICON_MAP).map(key => (
                                                <option key={key} value={key}>{key}</option>
                                            ))}
                                        </select>
                                        <small className="text-muted">Choose an icon representation for this category.</small>
                                    </div>
                                    <div className="category-form-actions">
                                        <button type="submit" className="btn-premium">
                                            {isEditingCategory ? 'Update Category' : 'Add Category'}
                                        </button>
                                        {isEditingCategory && (
                                            <button 
                                                type="button" 
                                                className="btn-cancel"
                                                onClick={() => {
                                                    setIsEditingCategory(null);
                                                    setCategoryFormData({ name: '', icon: 'Tag' });
                                                }}
                                            >
                                                Cancel
                                            </button>
                                        )}
                                    </div>
                                </form>
                            </div>

                            <div className="category-list-card glass-card">
                                <table className="category-table">
                                    <thead>
                                        <tr>
                                            <th>Icon</th>
                                            <th>Category Name</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {categories.map(cat => {
                                            const Icon = ICON_MAP[cat.icon || 'Tag'] || Tag;
                                            return (
                                                <tr key={cat._id}>
                                                    <td><Icon size={18} /></td>
                                                    <td className="font-bold">{cat.name}</td>
                                                    <td className="category-actions">
                                                        <button onClick={() => handleEditCategory(cat)} className="btn-icon">
                                                            <Edit3 size={14} />
                                                        </button>
                                                        <button onClick={() => handleDeleteCategory(cat._id)} className="btn-icon delete">
                                                            <Trash2 size={14} />
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </main>
                )}
                </div>
            </div>

            {/* Side Drawer */}
            <div className={`premium-drawer-overlay ${isCreating ? 'open' : ''}`} onClick={() => { setIsCreating(false); setError(''); setSuccess(''); }}>
                <div className="premium-drawer" onClick={(e) => e.stopPropagation()}>
                    <div className="drawer-header-premium">
                        <h2>{editingId ? 'Update Vendor' : 'Onboard Vendor'}</h2>
                        <button className="close-drawer" onClick={() => { setIsCreating(false); setError(''); setSuccess(''); }}>
                            <X size={24} />
                        </button>
                    </div>

                    <div className="drawer-body-premium">
                        {error && <div className="error-message" style={{ marginBottom: '1.5rem' }}>{error}</div>}
                        {success && <div className="success-message" style={{ marginBottom: '1.5rem' }}>{success}</div>}
                        
                        <form onSubmit={handleSubmit}>
                            <div className="premium-form-group">
                                <label>Business Name</label>
                                <input 
                                    className="premium-input" 
                                    type="text" 
                                    name="name" 
                                    value={formData.name} 
                                    onChange={handleInputChange} 
                                    required 
                                    placeholder="Enter premium business name" 
                                />
                            </div>

                            <div className="premium-form-group" style={{ flexDirection: 'row', alignItems: 'center', gap: '10px', marginBottom: '1.5rem' }}>
                                <input 
                                    type="checkbox" 
                                    name="isVerified" 
                                    checked={formData.isVerified} 
                                    onChange={handleInputChange}
                                    id="isVerified"
                                    style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                                />
                                <label htmlFor="isVerified" style={{ cursor: 'pointer', margin: 0, color: '#d4af37', fontWeight: 'bold' }}>
                                    ✨ Verified by Shadigram (Recommendation)
                                </label>
                            </div>

                            <div className="premium-form-group">
                                <label>Business Model</label>
                                <select className="premium-input" name="vendorType" value={formData.vendorType} onChange={handleInputChange} required>
                                    <option value="service">Booking / Service (Venues, Caterers, etc.)</option>
                                    <option value="product">Shopping / Product (Clothing, Jewelry, etc.)</option>
                                </select>
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
                                <div className="premium-form-group">
                                    <label>Category</label>
                                    <select className="premium-input" name="category" value={formData.category} onChange={handleInputChange} required>
                                        <option value="">Choose Service</option>
                                        {categories.map(c => (
                                            <option key={c._id} value={c.name}>{c.name}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="premium-form-group">
                                    <label>City / Region</label>
                                    <SearchableSelect
                                        isMulti={true}
                                        options={cities.map(c => ({ value: c.name, label: c.name }))}
                                        value={formData.city}
                                        onChange={(val) => setFormData({ ...formData, city: val })}
                                        placeholder="Select operational cities..."
                                    />
                                </div>
                            </div>

                            <div className="premium-form-group">
                                <label>Complete Address</label>
                                <input 
                                    className="premium-input" 
                                    type="text" 
                                    name="address" 
                                    value={formData.address} 
                                    onChange={handleInputChange} 
                                    placeholder="Enter business street address..." 
                                />
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
                                <div className="premium-form-group">
                                    <label>Primary Contact (Mobile)</label>
                                    <input 
                                        className="premium-input" 
                                        type="text" 
                                        name="primaryContact" 
                                        value={formData.primaryContact} 
                                        onChange={handleInputChange} 
                                        required 
                                        placeholder="+91 XXXXX XXXXX" 
                                    />
                                </div>
                                <div className="premium-form-group">
                                    <label>Secondary Contact</label>
                                    <input 
                                        className="premium-input" 
                                        type="text" 
                                        name="secondaryContact" 
                                        value={formData.secondaryContact} 
                                        onChange={handleInputChange} 
                                        placeholder="+91 XXXXX XXXXX" 
                                    />
                                </div>
                            </div>

                            <div className="premium-form-group">
                                <label>Official Email Address</label>
                                <input 
                                    className="premium-input" 
                                    type="email" 
                                    name="email" 
                                    value={formData.email} 
                                    onChange={handleInputChange} 
                                    placeholder="contact@business.com (Optional)" 
                                />
                            </div>

                            <div className="premium-form-group">
                                <label>Service Packages (Price Range)</label>
                                <div className="price-selection-grid">
                                    {priceRanges.map(range => (
                                        <div
                                            key={range}
                                            className={`price-chip ${selectedPriceRanges.includes(range) ? 'active' : ''}`}
                                            onClick={() => togglePriceRange(range)}
                                        >
                                            {range}
                                        </div>
                                    ))}
                                </div>
                            </div>



                            <div className="premium-form-group">
                                <label>Business Description</label>
                                <textarea 
                                    className="premium-input" 
                                    name="description" 
                                    value={formData.description} 
                                    onChange={handleInputChange} 
                                    rows={4} 
                                    placeholder="Highlight premium features, experience, and USPs..." 
                                />
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
                                <div className="premium-form-group">
                                    <label>Instagram Profile</label>
                                    <div className="relative">
                                        <Instagram size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted" />
                                        <input 
                                            className="premium-input" 
                                            style={{ paddingLeft: '3rem' }}
                                            type="text" 
                                            name="instagramUrl" 
                                            value={formData.instagramUrl} 
                                            onChange={handleInputChange} 
                                            placeholder="https://instagram.com/..." 
                                        />
                                    </div>
                                </div>
                                <div className="premium-form-group">
                                    <label>Facebook Page</label>
                                    <div className="relative">
                                        <Facebook size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted" />
                                        <input 
                                            className="premium-input" 
                                            style={{ paddingLeft: '3rem' }}
                                            type="text" 
                                            name="facebookUrl" 
                                            value={formData.facebookUrl} 
                                            onChange={handleInputChange} 
                                            placeholder="https://facebook.com/..." 
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="premium-form-group">
                                <label>Portfolio Showcase {editingId ? '(New & Existing)' : '(Max 5 Images)'}</label>
                                
                                {editingId && existingImages.length > 0 && (
                                    <div className="existing-images-section" style={{ marginBottom: '1.5rem' }}>
                                        <p style={{ fontSize: '0.85rem', marginBottom: '0.8rem', color: '#999' }}>Existing Portfolio Images:</p>
                                        <div className="previews-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))', gap: '10px' }}>
                                            {existingImages.map((url, index) => (
                                                <div key={index} className="preview-item" style={{ position: 'relative', aspectRatio: '1', borderRadius: '8px', overflow: 'hidden', border: '1px solid #d4af37' }}>
                                                    <img src={url} alt={`Existing ${index}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                    <button 
                                                        type="button"
                                                        onClick={() => removeExistingImage(index)}
                                                        style={{ position: 'absolute', top: '2px', right: '2px', background: 'rgba(239, 68, 68, 0.9)', color: 'white', border: 'none', borderRadius: '50%', width: '20px', height: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', zIndex: 4 }}
                                                        title="Delete existing image"
                                                    >
                                                        <Trash2 size={12} />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                <div className="premium-input image-upload-container" style={{ borderStyle: 'dashed', position: 'relative', minHeight: '120px' }}>
                                    <input 
                                        type="file" 
                                        multiple 
                                        onChange={handleFileChange} 
                                        accept="image/*" 
                                        className="absolute inset-0 opacity-0 cursor-pointer"
                                        style={{ zIndex: 2 }}
                                    />
                                    {imagePreviews.length === 0 ? (
                                        <div className="flex flex-col items-center py-4 text-muted">
                                            <Upload size={32} strokeWidth={1} />
                                            <p className="mt-2 font-semibold">{editingId ? 'Add more portfolio photos' : 'Click to upload portfolio photos'}</p>
                                            <p className="text-[10px] uppercase tracking-widest mt-1">
                                                {editingId ? `You can add up to ${5 - existingImages.length} more` : 'Max 5 images • Max size 5MB per image'}
                                            </p>
                                        </div>
                                    ) : (
                                        <div className="previews-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))', gap: '10px', padding: '10px', width: '100%', zIndex: 3, position: 'relative' }}>
                                            {imagePreviews.map((url, index) => (
                                                <div key={index} className="preview-item" style={{ position: 'relative', aspectRatio: '1', borderRadius: '8px', overflow: 'hidden', border: '1px solid #d4af37' }}>
                                                    <img src={url} alt={`Preview ${index}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                    <button 
                                                        type="button"
                                                        onClick={(e) => { e.stopPropagation(); removeImage(index); }}
                                                        style={{ position: 'absolute', top: '2px', right: '2px', background: 'rgba(0,0,0,0.6)', color: 'white', border: 'none', borderRadius: '50%', width: '20px', height: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', zIndex: 4 }}
                                                    >
                                                        <X size={12} />
                                                    </button>
                                                </div>
                                            ))}
                                            {(existingImages.length + imagePreviews.length) < 5 && (
                                                <div className="add-more-placeholder" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px dashed #d4af37', borderRadius: '8px', aspectRatio: '1', color: '#d4af37' }}>
                                                    <Plus size={20} />
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                                {(imageFiles.length > 0 || existingImages.length > 0) && (
                                    <p style={{ fontSize: '0.75rem', marginTop: '0.5rem', color: '#d4af37' }}>
                                        {existingImages.length} existing + {imageFiles.length} new images (Max 5 total)
                                    </p>
                                )}
                            </div>
                        </form>
                    </div>

                    <div className="drawer-footer-premium">
                        <button className="btn-luxury-outline" onClick={() => setIsCreating(false)}>Discard</button>
                        <button className="btn-luxury" onClick={(e: any) => handleSubmit(e)} disabled={loading}>
                            {loading ? 'Processing...' : (editingId ? 'Apply Changes' : 'Publish to Marketplace')}
                        </button>
                    </div>
                </div>
            </div>
            {selectedVendorForAvail && (
                <VendorAvailabilityManager 
                    vendorId={selectedVendorForAvail.id}
                    vendorName={selectedVendorForAvail.name}
                    onClose={() => setSelectedVendorForAvail(null)}
                />
            )}
            {selectedVendorForPkgs && (
                <VendorPackagesManager 
                    vendorId={selectedVendorForPkgs.id}
                    vendorName={selectedVendorForPkgs.name}
                    initialPackages={selectedVendorForPkgs.packages}
                    onClose={() => setSelectedVendorForPkgs(null)}
                    onSaveSuccess={fetchVendors}
                    accessToken={accessToken || ''}
                />
            )}
        </div>
    );
};

interface VendorPackagesManagerProps {
    vendorId: string;
    vendorName: string;
    initialPackages: Array<{ name: string; price: number; priceUnit?: string; description?: string; features?: string[] }>;
    onClose: () => void;
    onSaveSuccess: () => void;
    accessToken: string;
}

const VendorPackagesManager: React.FC<VendorPackagesManagerProps> = ({ vendorId, vendorName, initialPackages, onClose, onSaveSuccess, accessToken }) => {
    const [pkgs, setPkgs] = useState(initialPackages);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const handleSave = async () => {
        setSaving(true);
        setError('');
        try {
            const res = await fetch(`${API_BASE_URL}/api/market-vendors/${vendorId}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${accessToken}`
                },
                body: JSON.stringify({ packages: pkgs })
            });

            if (res.ok) {
                onSaveSuccess();
                onClose();
            } else {
                const data = await res.json();
                setError(data.message || 'Failed to update packages');
            }
        } catch (err) {
            setError('Error saving packages to server');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="premium-drawer-overlay open" onClick={onClose} style={{ zIndex: 1000, display: 'flex', justifyContent: 'flex-end' }}>
            <div className="premium-drawer" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px', width: '100%', height: '100%', display: 'flex', flexDirection: 'column', background: '#0e0e0e' }}>
                <div className="drawer-header-premium" style={{ display: 'flex', justifyContent: 'space-between', padding: '1.5rem', borderBottom: '1px solid rgba(212,175,55,0.1)' }}>
                    <div>
                        <h2 style={{ color: '#d4af37', margin: 0, fontSize: '1.5rem' }}>Manage Packages & Plans</h2>
                        <p style={{ margin: '4px 0 0 0', fontSize: '0.85rem', color: '#999' }}>{vendorName}</p>
                    </div>
                    <button className="close-drawer" onClick={onClose} style={{ background: 'transparent', border: 'none', color: '#d4af37', cursor: 'pointer' }}>
                        <X size={24} />
                    </button>
                </div>

                <div className="drawer-body-premium" style={{ flex: 1, overflowY: 'auto', padding: '1.5rem' }}>
                    {error && <div className="error-message" style={{ background: 'rgba(255,77,77,0.1)', color: '#ff4d4d', padding: '10px', borderRadius: '4px', marginBottom: '1.5rem' }}>{error}</div>}

                    <div className="packages-list-admin" style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '20px' }}>
                        <h3 style={{ margin: '0 0 10px 0', fontSize: '1.1rem', color: '#d4af37' }}>Active Packages</h3>
                        {pkgs.length === 0 ? (
                            <div style={{ textAlign: 'center', padding: '30px 10px', color: '#888', border: '1px dashed rgba(212,175,55,0.2)', borderRadius: '8px', background: 'rgba(255,255,255,0.01)' }}>
                                No custom packages defined for this vendor yet. Add one below!
                            </div>
                        ) : (
                            pkgs.map((pkg, idx) => (
                                <div key={idx} className="package-item-card" style={{ display: 'flex', flexDirection: 'column', border: '1px solid rgba(212, 175, 55, 0.3)', borderRadius: '8px', padding: '15px', background: 'rgba(255, 255, 255, 0.05)', position: 'relative' }}>
                                    <button 
                                        type="button" 
                                        onClick={() => setPkgs(prev => prev.filter((_, i) => i !== idx))}
                                        style={{ position: 'absolute', top: '10px', right: '10px', background: 'transparent', border: 'none', color: '#ff4d4d', cursor: 'pointer' }}
                                    >
                                        <Trash2 size={16} />
                                    </button>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold', color: '#d4af37', fontSize: '1.05rem' }}>
                                        <span>{pkg.name}</span>
                                        <span>₹{pkg.price.toLocaleString('en-IN')}{pkg.priceUnit ? ` / ${pkg.priceUnit}` : ''}</span>
                                    </div>
                                    {pkg.description && <p style={{ fontSize: '0.85rem', color: '#ccc', margin: '6px 0 0 0', lineHeight: '1.4' }}>{pkg.description}</p>}
                                    {pkg.features && pkg.features.length > 0 && (
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '10px' }}>
                                            {pkg.features.map((f, fi) => (
                                                <span key={fi} style={{ fontSize: '0.75rem', background: 'rgba(212,175,55,0.1)', color: '#d4af37', padding: '2px 8px', borderRadius: '4px' }}>✓ {f}</span>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))
                        )}
                    </div>

                    {/* Add new package sub-form */}
                    <div className="add-package-subform" style={{ border: '1px dashed #d4af37', borderRadius: '8px', padding: '20px', background: 'rgba(255, 255, 255, 0.02)' }}>
                        <h4 style={{ margin: '0 0 15px 0', color: '#d4af37', fontSize: '1rem', fontWeight: 'bold' }}>Add a New Package</h4>
                        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '15px', marginBottom: '15px' }}>
                            <div className="premium-form-group" style={{ marginBottom: 0 }}>
                                <label style={{ fontSize: '0.8rem', color: '#999', display: 'block', marginBottom: '4px' }}>Package Name</label>
                                <input 
                                    type="text" 
                                    id="modal-pkg-name" 
                                    className="premium-input" 
                                    placeholder="e.g. Premium Photography" 
                                    style={{ margin: 0, width: '100%', boxSizing: 'border-box' }}
                                />
                            </div>
                            <div className="premium-form-group" style={{ marginBottom: 0 }}>
                                <label style={{ fontSize: '0.8rem', color: '#999', display: 'block', marginBottom: '4px' }}>Price (₹)</label>
                                <input 
                                    type="number" 
                                    id="modal-pkg-price" 
                                    className="premium-input" 
                                    placeholder="e.g. 75000" 
                                    style={{ margin: 0, width: '100%', boxSizing: 'border-box' }}
                                />
                            </div>
                            <div className="premium-form-group" style={{ marginBottom: 0 }}>
                                <label style={{ fontSize: '0.8rem', color: '#999', display: 'block', marginBottom: '4px' }}>Unit (e.g. per plate, per day)</label>
                                <input 
                                    type="text" 
                                    id="modal-pkg-unit" 
                                    className="premium-input" 
                                    placeholder="e.g. per day" 
                                    style={{ margin: 0, width: '100%', boxSizing: 'border-box' }}
                                />
                            </div>
                        </div>
                        <div className="premium-form-group" style={{ marginBottom: '15px' }}>
                            <label style={{ fontSize: '0.8rem', color: '#999', display: 'block', marginBottom: '4px' }}>Description</label>
                            <textarea 
                                id="modal-pkg-desc" 
                                className="premium-input" 
                                placeholder="Details and deliverables of this plan..." 
                                rows={2}
                                style={{ width: '100%', boxSizing: 'border-box' }}
                            />
                        </div>
                        <div className="premium-form-group" style={{ marginBottom: '20px' }}>
                            <label style={{ fontSize: '0.8rem', color: '#999', display: 'block', marginBottom: '4px' }}>Features / Deliverables (comma separated)</label>
                            <input 
                                type="text" 
                                id="modal-pkg-features" 
                                className="premium-input" 
                                placeholder="e.g. 2 Photographers, Pre-wedding shoot, Drone shots" 
                                style={{ width: '100%', boxSizing: 'border-box' }}
                            />
                        </div>
                        <button 
                            type="button" 
                            className="btn-luxury-outline" 
                            style={{ padding: '8px 16px', fontSize: '0.9rem', width: '100%', cursor: 'pointer' }}
                            onClick={() => {
                                const nameEl = document.getElementById('modal-pkg-name') as HTMLInputElement;
                                const priceEl = document.getElementById('modal-pkg-price') as HTMLInputElement;
                                const unitEl = document.getElementById('modal-pkg-unit') as HTMLInputElement;
                                const descEl = document.getElementById('modal-pkg-desc') as HTMLInputElement;
                                const featuresEl = document.getElementById('modal-pkg-features') as HTMLInputElement;

                                if (!nameEl || !priceEl || !nameEl.value || !priceEl.value) {
                                    alert('Package Name and Price are required.');
                                    return;
                                }

                                const newPkg = {
                                    name: nameEl.value,
                                    price: Number(priceEl.value),
                                    priceUnit: unitEl ? unitEl.value : '',
                                    description: descEl ? descEl.value : '',
                                    features: featuresEl && featuresEl.value ? featuresEl.value.split(',').map(f => f.trim()).filter(Boolean) : []
                                };

                                setPkgs(prev => [...prev, newPkg]);

                                // Clear inputs
                                nameEl.value = '';
                                priceEl.value = '';
                                if (unitEl) unitEl.value = '';
                                if (descEl) descEl.value = '';
                                if (featuresEl) featuresEl.value = '';
                            }}
                        >
                            + Add to Packages List
                        </button>
                    </div>
                </div>

                <div className="drawer-footer-premium" style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', padding: '1.5rem', borderTop: '1px solid rgba(212,175,55,0.1)' }}>
                    <button className="btn-luxury-outline" onClick={onClose} style={{ cursor: 'pointer' }}>Discard</button>
                    <button className="btn-luxury" onClick={handleSave} disabled={saving} style={{ cursor: 'pointer' }}>
                        {saving ? 'Saving...' : 'Apply & Save Plans'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SuperAdminMarketplace;
